import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { formatDate } from '../../core/models/DateFormatter';
import { ImageThumb } from '../../core/shared/components/ImageThumb';
import { VendorUpdateList } from '../workorder/shared/VendorUpdatesCard';
import { VENDOR_UPDATE_TYPES, VendorUpdate } from '../workorder/shared/vendorLink';

// Own axios instance: the app-wide interceptors send anyone without a login to the sign-in page.
const api = axios.create({ baseURL: process.env.REACT_APP_MANUFACTURING_API_URL });

type WorkOrderLink = {
  organizationName?: string | null;
  vendorName: string;
  processName: string;
  orderNumber: string;
  workOrderDate?: string | null;
  expectedDeliveryDate?: string | null;
  statusName?: string | null;
  isOpen: boolean;
  items: { designNumberName: string; designNumberImagePath?: string | null; quantity: number }[];
  documents: { name: string; url: string }[];
  updates: VendorUpdate[];
};

// The WhatsApp button carries "{tenant}-{32 hex chars}"; hex has no '-', so the last '-' splits them.
const splitKey = (key: string) => {
  const i = key.lastIndexOf('-');
  return { tenant: key.slice(0, Math.max(i, 0)), secret: key.slice(i + 1) };
};

const errorMessage = (err: any) =>
  err?.response?.data?.messages?.[0] ?? 'Something went wrong. Please try again.';

const WorkOrderLinkPage = () => {
  const { key = '' } = useParams();
  const { tenant, secret } = splitKey(key);
  const headers = { tenant };

  const [updateType, setUpdateType] = useState<number>();
  const [notes, setNotes] = useState('');
  const [revisedDeliveryDate, setRevisedDeliveryDate] = useState('');

  const { data, isLoading, isError, refetch } = useQuery(
    [`work-order-link-${key}`],
    () => api.get<WorkOrderLink>(`/workorderlink/${secret}`, { headers }).then((r) => r.data),
    { retry: false, enabled: !!tenant && !!secret }
  );

  const { mutate, isLoading: isSaving } = useMutation(
    () =>
      api
        .post(
          `/workorderlink/${secret}/updates`,
          { updateType, notes: notes || null, revisedDeliveryDate: revisedDeliveryDate || null },
          { headers }
        )
        .then((r) => r.data),
    {
      onSuccess: (result) => {
        if (!result.succeeded) {
          toast.error(result.messages?.[0] ?? 'Could not send the update.');
          return;
        }
        toast.success(result.messages?.[0] ?? 'Update sent.');
        setUpdateType(undefined);
        setNotes('');
        setRevisedDeliveryDate('');
        refetch();
      },
      onError: (err) => toast.error(errorMessage(err)),
    }
  );

  if (isLoading && !!tenant && !!secret) {
    return (
      <div className='d-flex justify-content-center p-20 text-muted'>Loading work order...</div>
    );
  }

  if (isError || !data) {
    return (
      <div className='d-flex justify-content-center p-20'>
        <div className='text-center'>
          <h2>This link is not valid</h2>
          <p className='text-muted'>
            Please check the latest WhatsApp message for this work order.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-light min-vh-100 py-6'>
      <div className='container px-4' style={{ maxWidth: 640 }}>
        <div className='card mb-5'>
          <div className='card-body'>
            {data.organizationName && (
              <div className='text-muted fs-7 mb-1'>{data.organizationName}</div>
            )}
            <h1 className='fs-2 mb-1'>
              {data.processName} work order {data.orderNumber}
            </h1>
            <div className='text-gray-700 mb-3'>For {data.vendorName}</div>
            <div className='d-flex flex-wrap gap-4 fs-7'>
              {data.statusName && <span className='badge badge-light-info'>{data.statusName}</span>}
              {data.workOrderDate && (
                <span>Issued: {formatDate(data.workOrderDate, 'dd MMM yyyy')}</span>
              )}
              {data.expectedDeliveryDate && (
                <span className='fw-bold'>
                  Deliver by: {formatDate(data.expectedDeliveryDate, 'dd MMM yyyy')}
                </span>
              )}
            </div>
            {data.documents.length > 0 && (
              <div className='mt-4 d-flex flex-wrap gap-2'>
                {data.documents.map((d) => (
                  <a
                    key={d.url}
                    href={d.url}
                    target='_blank'
                    rel='noreferrer'
                    className='btn btn-sm btn-light-primary'
                  >
                    <i className='ki-duotone ki-file-down fs-3' /> {d.name} PDF
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className='card mb-5'>
          <div className='card-body'>
            <h3 className='fs-4 mb-4'>Items</h3>
            {data.items.map((item, i) => (
              <div
                key={i}
                className='d-flex align-items-center justify-content-between border-bottom border-gray-200 py-2'
              >
                <span>
                  <ImageThumb imagePath={item.designNumberImagePath} alt={item.designNumberName} />
                  {item.designNumberName}
                </span>
                <span className='fw-bold'>Qty {item.quantity}</span>
              </div>
            ))}
          </div>
        </div>

        <div className='card mb-5'>
          <div className='card-body'>
            <h3 className='fs-4 mb-4'>Send an update</h3>
            {data.isOpen ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  mutate();
                }}
              >
                <div className='d-grid gap-2 mb-4'>
                  {Object.entries(VENDOR_UPDATE_TYPES).map(([value, label]) => (
                    <button
                      key={value}
                      type='button'
                      aria-pressed={updateType === Number(value)}
                      className={`btn btn-lg ${updateType === Number(value) ? 'btn-primary' : 'btn-light-primary'}`}
                      onClick={() => setUpdateType(Number(value))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <label htmlFor='wo-notes' className='form-label'>
                  Note (optional)
                </label>
                <textarea
                  id='wo-notes'
                  className='form-control mb-4'
                  rows={3}
                  maxLength={500}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <label htmlFor='wo-revised-date' className='form-label'>
                  New delivery date (optional)
                </label>
                <input
                  id='wo-revised-date'
                  type='date'
                  className='form-control mb-5'
                  min={format(new Date(), 'yyyy-MM-dd')}
                  value={revisedDeliveryDate}
                  onChange={(e) => setRevisedDeliveryDate(e.target.value)}
                />
                <button
                  type='submit'
                  className='btn btn-success btn-lg w-100'
                  disabled={!updateType || isSaving}
                >
                  {isSaving ? 'Sending...' : 'Send update'}
                </button>
              </form>
            ) : (
              <div className='text-muted'>
                This work order is closed, so no more updates can be sent.
              </div>
            )}
          </div>
        </div>

        <div className='card mb-5'>
          <div className='card-body'>
            <h3 className='fs-4 mb-4'>Your updates</h3>
            <VendorUpdateList updates={data.updates} />
          </div>
        </div>
      </div>
    </div>
  );
};

export { WorkOrderLinkPage };
